import { Text } from '@/ui-kit'
import { Trans } from '@lingui/react/macro'
import { AlertCircle } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { useMyConversationSessions } from '../../api/conversations'
import { extractApiError, joinSessionByCode } from '../../api/conversations-api'
import type { ProjectFlowProps } from '../project-flows'
import { ConversationModeStep } from './steps/ConversationModeStep'
import { IneligibleStep } from './steps/IneligibleStep'
import { WaitingForSessionStep } from './steps/WaitingForSessionStep'
import { StepSession } from './StepSession'

export const VoiceConversationFlow = ({
  projectId,
  projectName,
  rewardConfig,
  progress,
  maxTasksPerUser,
  onBack,
  onCompleted
}: ProjectFlowProps) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const inviteCode = searchParams.get('inviteCode')
  const { data: sessions = [], isLoading, error } = useMyConversationSessions(projectId)
  const [sessionCreated, setSessionCreated] = useState(false)
  const [joiningByCode, setJoiningByCode] = useState(!!inviteCode)
  const [hasActiveSessionError, setHasActiveSessionError] = useState(false)
  const [isIneligible, setIsIneligible] = useState(false)

  useEffect(() => {
    if (!inviteCode) return

    const joinByCode = async () => {
      try {
        const result = await joinSessionByCode(inviteCode)
        if (result.status === 'has_active_session') {
          setHasActiveSessionError(true)
        } else if (result.status === 'ineligible') {
          setIsIneligible(true)
        } else {
          setSessionCreated(true)
        }
      } catch (err) {
        toast.error(extractApiError(err, 'Failed to join conversation'))
      } finally {
        setJoiningByCode(false)
        searchParams.delete('inviteCode')
        setSearchParams(searchParams, { replace: true })
      }
    }

    joinByCode()
  }, [inviteCode])

  if (joiningByCode) {
    return <WaitingForSessionStep onBack={onBack} />
  }

  if (isIneligible) {
    return <IneligibleStep />
  }

  if (isLoading) {
    return <WaitingForSessionStep onBack={onBack} />
  }

  return (
    <>
      {hasActiveSessionError && (
        <div className="border-orange/40 bg-orange/10 mx-auto flex max-w-[800px] items-start gap-3 rounded-xl border border-solid px-4 py-3">
          <AlertCircle className="text-orange mt-0.5 size-4 shrink-0" />
          <p className="text-orange text-sm">
            <Trans>
              You cannot join another conversation while a conversation is in progress. Please complete this
              conversation first.
            </Trans>
          </p>
        </div>
      )}
      {sessionCreated || (!isLoading && !error && sessions.length > 0) ? (
        <StepSession
          projectId={projectId}
          projectName={projectName}
          rewardConfig={{ type: rewardConfig?.type ?? 'points', amount: rewardConfig?.amount ?? 0 }}
          onBack={onBack}
          onCompleted={onCompleted}
        />
      ) : (
        <>
          <Text type="h4" className="-mb-2">
            {projectName}
          </Text>
          {maxTasksPerUser > 0 && progress.completed >= maxTasksPerUser ? (
            <IneligibleStep />
          ) : (
            <ConversationModeStep projectId={projectId} onSuccess={() => setSessionCreated(true)} />
          )}
        </>
      )}
    </>
  )
}
